// Firebase Topics Service
// ========================
// CRUD operations for topics and subtopics within learning journeys

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db } from './config';
import type { Topic, Subtopic, TeachingForm } from '../../types';

/**
 * Get collection reference for journey topics
 * Topics are stored as a subcollection under learningJourneys
 */
const getJourneyTopicsRef = (journeyId: string) =>
  collection(db, 'learningJourneys', journeyId, 'topics');

/**
 * Create a new topic under a journey
 * @param journeyId The learning journey's ID
 * @param topic Topic data (without id)
 * @returns Result with success status and created topic id
 */
export const createTopic = async (
  journeyId: string,
  topic: Omit<Topic, 'id'>
): Promise<{ success: boolean; id?: string; error?: string }> => {
  try {
    const topicData = {
      ...topic,
      learningJourneyId: journeyId,
      createdAt: Timestamp.now(),
    };

    const docRef = await addDoc(getJourneyTopicsRef(journeyId), topicData);
    console.log('[Firebase] Topic created:', docRef.id);
    return { success: true, id: docRef.id };
  } catch (error: any) {
    console.error('[Firebase] Error creating topic:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create multiple topics at once (batch operation)
 * Also updates the journey.topicIds array to keep it in sync
 * @param journeyId The learning journey's ID
 * @param topics Array of topics to create
 * @returns Result with success status and created topic ids
 */
export const createTopicsBatch = async (
  journeyId: string,
  topics: Omit<Topic, 'id'>[]
): Promise<{ success: boolean; ids?: string[]; error?: string }> => {
  try {
    const ids: string[] = [];

    for (const topic of topics) {
      const result = await createTopic(journeyId, topic);
      if (result.success && result.id) {
        ids.push(result.id);
      }
    }

    // Update journey.topicIds to keep it in sync with subcollection
    // This ensures TeachingPage and other components show correct topic count
    if (ids.length > 0) {
      try {
        const journeyRef = doc(db, 'learningJourneys', journeyId);
        await updateDoc(journeyRef, {
          topicIds: arrayUnion(...ids),
        });
      } catch (updateError) {
        // Log but don't fail - topics were created successfully
        console.warn('[Firebase] Could not update journey.topicIds:', updateError);
      }
    }

    return { success: true, ids };
  } catch (error: any) {
    console.error('[Firebase] Error creating topics batch:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all topics for a journey
 * @param journeyId The learning journey's ID
 * @returns Array of topics with their subtopics
 */
export const getTopicsByJourney = async (
  journeyId: string
): Promise<{ success: boolean; topics?: Topic[]; error?: string }> => {
  try {
    const topicsRef = getJourneyTopicsRef(journeyId);
    // Query without orderBy to ensure ALL topics are returned,
    // even those missing the 'order' field (Firestore excludes
    // documents without the ordered field from query results)
    const snapshot = await getDocs(topicsRef);

    const topics = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    })) as Topic[];

    // Sort client-side - topics without order field go last
    topics.sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

    return { success: true, topics };
  } catch (error: any) {
    console.error('[Firebase] Error getting topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get a single topic by ID
 * @param journeyId The learning journey's ID
 * @param topicId The topic's ID
 * @returns The topic or null if not found
 */
export const getTopic = async (
  journeyId: string,
  topicId: string
): Promise<{ success: boolean; topic?: Topic; error?: string }> => {
  try {
    const docRef = doc(db, 'learningJourneys', journeyId, 'topics', topicId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { success: false, error: 'Topic not found' };
    }

    return {
      success: true,
      topic: { id: docSnap.id, ...docSnap.data() } as Topic,
    };
  } catch (error: any) {
    console.error('[Firebase] Error getting topic:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update a topic
 * @param journeyId The learning journey's ID
 * @param topicId The topic's ID
 * @param data Partial topic data to update
 * @returns Result with success status
 */
export const updateTopic = async (
  journeyId: string,
  topicId: string,
  data: Partial<Omit<Topic, 'id'>>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, 'learningJourneys', journeyId, 'topics', topicId);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  } catch (error: any) {
    console.error('[Firebase] Error updating topic:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a topic
 * Also removes the topic ID from journey.topicIds to keep it in sync
 * @param journeyId The learning journey's ID
 * @param topicId The topic's ID
 * @returns Result with success status
 */
export const deleteTopic = async (
  journeyId: string,
  topicId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, 'learningJourneys', journeyId, 'topics', topicId);
    await deleteDoc(docRef);

    // Also remove from journey.topicIds to keep in sync
    try {
      const journeyRef = doc(db, 'learningJourneys', journeyId);
      await updateDoc(journeyRef, {
        topicIds: arrayRemove(topicId),
      });
    } catch (updateError) {
      // Log but don't fail - topic was deleted successfully
      console.warn('[Firebase] Could not update journey.topicIds after delete:', updateError);
    }

    console.log('[Firebase] Topic deleted:', topicId);
    return { success: true };
  } catch (error: any) {
    console.error('[Firebase] Error deleting topic:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete all topics for a journey (used when deleting a journey)
 * @param journeyId The journey's ID
 * @returns Result with success status and count of deleted topics
 */
export const deleteAllJourneyTopics = async (
  journeyId: string
): Promise<{ success: boolean; deletedCount?: number; error?: string }> => {
  try {
    const result = await getTopicsByJourney(journeyId);

    if (!result.success || !result.topics) {
      return { success: true, deletedCount: 0 };
    }

    let deletedCount = 0;
    for (const topic of result.topics) {
      const deleteResult = await deleteTopic(journeyId, topic.id);
      if (deleteResult.success) {
        deletedCount++;
      }
    }

    console.log(`[Firebase] Deleted ${deletedCount} topics for journey ${journeyId}`);
    return { success: true, deletedCount };
  } catch (error: any) {
    console.error('[Firebase] Error deleting all journey topics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update subtopics for a topic (replaces all subtopics)
 * @param journeyId The learning journey's ID
 * @param topicId The topic's ID
 * @param subtopics New subtopics array
 * @returns Result with success status
 */
export const updateTopicSubtopics = async (
  journeyId: string,
  topicId: string,
  subtopics: Subtopic[]
): Promise<{ success: boolean; error?: string }> => {
  try {
    const docRef = doc(db, 'learningJourneys', journeyId, 'topics', topicId);
    await updateDoc(docRef, {
      subtopics,
      updatedAt: Timestamp.now(),
    });
    return { success: true };
  } catch (error: any) {
    console.error('[Firebase] Error updating subtopics:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Mark a subtopic as completed
 * @param journeyId The learning journey's ID
 * @param topicId The topic's ID
 * @param subtopicId The subtopic's ID
 * @param completed Whether the subtopic is completed
 * @returns Result with success status
 */
/**
 * Record that a subtopic was addressed in a teaching session.
 * Increments touchCount and records the teaching form used.
 */
export const recordSubtopicTouch = async (
  journeyId: string,
  topicId: string,
  subtopicId: string,
  teachingForm: TeachingForm
): Promise<{ success: boolean; error?: string }> => {
  try {
    const topicResult = await getTopic(journeyId, topicId);
    if (!topicResult.success || !topicResult.topic) {
      return { success: false, error: 'Topic not found' };
    }

    const now = Timestamp.now().toDate().toISOString();
    const updatedSubtopics = (topicResult.topic.subtopics || []).map((sub) =>
      sub.id === subtopicId
        ? {
            ...sub,
            touchCount: (sub.touchCount || 0) + 1,
            lastTeachingForm: teachingForm,
            lastTouchedAt: now,
          }
        : sub
    );

    return updateTopicSubtopics(journeyId, topicId, updatedSubtopics);
  } catch (error: any) {
    console.error('[Firebase] Error recording subtopic touch:', error);
    return { success: false, error: error.message };
  }
};

/** @deprecated Use recordSubtopicTouch instead */
export const markSubtopicCompleted = async (
  journeyId: string,
  topicId: string,
  subtopicId: string,
  completed: boolean
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Get the current topic
    const topicResult = await getTopic(journeyId, topicId);
    if (!topicResult.success || !topicResult.topic) {
      return { success: false, error: 'Topic not found' };
    }

    // Update the specific subtopic
    const updatedSubtopics = (topicResult.topic.subtopics || []).map((sub) =>
      sub.id === subtopicId ? { ...sub, isCompleted: completed, completed } : sub
    );

    return updateTopicSubtopics(journeyId, topicId, updatedSubtopics);
  } catch (error: any) {
    console.error('[Firebase] Error marking subtopic completed:', error);
    return { success: false, error: error.message };
  }
};
